import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { ConsultationRequestAttachmentType } from '@enums/consultation-request-attachment-type';
import { ConsultationRequestOrderStatus } from '@enums/consultation-request-order-status';
import { ConsultationTreatmentTypes } from '@enums/consultation-treatment-types';
import { CustomerTestsTypes } from '@enums/customer-tests-types';
import { QuestionnaireAnswers } from '@enums/questionnaire-answers';
import { Symptom } from '@enums/symptoms';
import { Treatment } from '@enums/treatment';
import { UpsellSlugs } from '@enums/upsell-slugs';
import { environment } from '@environments/environment';
import { ConsultationRequestOrderCanceledError } from '@errors/consultation-request-order-canceled-error';
import { MissingStoreAttachmentTokenError } from '@errors/missing-store-attachment-token-error';
import { BasicConsultationRequestRequest } from '@models/consultation-request/basic-consultation-request-request';
import { BasicConsultationRequestResponse } from '@models/consultation-request/basic-consultation-request-response';
import { ConsultationQuestionAnswer } from '@models/consultation-request/consultation-question-answer';
import { ConsultationRequest } from '@models/consultation-request/consultation-request';
import { ConsultationRequestOrderDetail } from '@models/consultation-request/consultation-request-order-detail';
import { ConsultationRequestRequest } from '@models/consultation-request/consultation-request-request';
import { ConsultationSubscriptionDetails } from '@models/consultation-subscription-details';
import { AbstractSubscriptionBillingUpdateRequest } from '@models/requests/abstract-subscription-billing-update-request.model';
import { ConsultationStorageService } from '@services/consultation-storage.service';
import { ErrorLoggingService } from '@services/error-logging.service';
import { FileService } from '@services/file.service';
import { FormService } from '@services/form.service';
import { IndexedDbService } from '@services/indexed-db.service';
import { PaymentService } from '@services/payment.service';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ConsultationRequestService {
  public static cost = 9500;
  public static symptoms = Object.keys(Symptom).map((key) => ({ name: key, value: Symptom[key] }));
  public static partnerTreatmentSTDs = ['chlamydia', 'gonorrhea', 'trichomoniasis'];
  public readonly patRequestHeaderName = 'AH-request-token';

  /**
   * This property holds the consultation request order detail, and we lose it if the patient refreshes the page.
   * We need to find a better way to persist this data.
   *
   * @Issue: https://github.com/Medology/ng-checkout/issues/3680
   */
  protected consultationRequestOrderDetail: ConsultationRequestOrderDetail;
  protected subscriptionDetails: ConsultationSubscriptionDetails | null = null;

  protected http = inject(HttpClient);
  protected formService = inject(FormService);
  protected consultationStorageService = inject(ConsultationStorageService);
  protected fileService = inject(FileService);
  protected indexedDbService = inject(IndexedDbService);
  protected errorLoggingService = inject(ErrorLoggingService);

  private router = inject(Router);
  private paymentService = inject(PaymentService);

  private apiUrl = `${environment.analyteCareApi}/api/v1`;
  private apiV2Url = `${environment.analyteCareApi}/api/v2`;
  private apiConsultationUrl = `${this.apiUrl}/consultation`;
  private readonly anonymousTokenResponseHeaderName = 'AH-anonymous-token';

  /**
   * Get consultation request form group from form service.
   */
  public get consultationForm(): FormGroup {
    return this.formService.consultationRequest;
  }

  /**
   * Get the consultation form treatment control.
   */
  public get treatmentControl(): AbstractControl {
    return this.consultationForm.get('prescription').get('treatment');
  }

  /**
   * Get the consultation order detail.
   */
  public get consultationOrderDetail(): ConsultationRequestOrderDetail {
    return this.consultationRequestOrderDetail;
  }

  /**
   * Get the subscription details retrieved for the consultation order, if available.
   */
  public get consultationSubscriptionDetails(): ConsultationSubscriptionDetails | null {
    return this.subscriptionDetails;
  }

  /**
   * Whether the consultation order belongs to an external subscription.
   */
  public get isExternalSubscription(): boolean {
    return !!this.consultationOrderDetail?.subscription?.external_subscription_id;
  }

  /**
   * Retrieves upsells requested by the patient during the consultation request flow.
   *
   * @returns {ConsultationQuestionAnswer} an object containing the question "Additional Requests" and an answer with
   * comma-separated requested upsell names or "No" if none were requested
   */
  public getAdditionalRequests(): ConsultationQuestionAnswer {
    const upsells = this.consultationStorageService.upsells.filter(
      (upsell) => !environment.consultationRequestUpsells[upsell]?.skipOrderAddon,
    );

    // Remove duplicates with Set, filter out upsells that don't have a price, and join them with a comma
    const additionalRequests = [...new Set([...upsells, ...this.consultationStorageService.getOrderedUpsellNames()])]
      .filter((upsell) => !!environment.consultationRequestUpsells[upsell]?.price)
      .filter((upsell) => !environment.consultationRequestUpsells[upsell]?.skipAdditionalRequest)
      .join(', ');

    return {
      question: 'Additional Requests',
      answer: additionalRequests || QuestionnaireAnswers.No,
    };
  }

  /**
   * Creates a consultation request payload specifically for STD consultation updates.
   *
   * @returns {ConsultationRequestRequest} a consultation request object configured for STD consultation updates
   */
  public getUpdateStdConsultationRequestPayload(): ConsultationRequestRequest {
    const consultationRequest = new ConsultationRequestRequest();

    consultationRequest.additional_info = JSON.stringify({
      ...(this.consultationStorageService.consultationAdditionalInfo || {}),
      questionnaire: [this.getAdditionalRequests()],
    });

    consultationRequest.treatment_for = this.consultationStorageService.treatmentFor;

    if (this.shouldUseDeliveryPharmacy()) {
      consultationRequest.pharmacy_id = environment.consultation.deliveryPharmacyId;
    }

    return consultationRequest;
  }

  /**
   * Determines if delivery pharmacy should be used based on medication delivery upsell.
   *
   * @returns {boolean} true if delivery address exists, false otherwise
   */
  public shouldUseDeliveryPharmacy(): boolean {
    return !!this.consultationStorageService.getUpsellAddress(UpsellSlugs.DeliverMedication);
  }

  /**
   * Saves consultation order details to local storage via the consultation storage service.
   */
  public saveConsultationOrderDetailToLocalStorage() {
    this.consultationStorageService.consultationId = this.consultationOrderDetail.consultationRequest.id || '';
    this.consultationStorageService.orderedTests = this.consultationOrderDetail.tests;
    this.consultationStorageService.customerTests = this.consultationOrderDetail.customer_tests;
    this.consultationStorageService.recharge = this.consultationOrderDetail.recharge;
    this.consultationStorageService.remainingBalance = this.consultationOrderDetail.remaining_balance;
    this.consultationStorageService.patient = {
      ...(this.consultationStorageService.patient || {}),
      name: this.consultationOrderDetail.first_name,
      last_name: this.consultationOrderDetail.last_name,
      email: this.consultationOrderDetail.email,
      phone: this.consultationOrderDetail.phone,
    };
    this.consultationStorageService.treatmentType = this.consultationOrderDetail.consultationRequest.treatment_type;
  }

  /**
   * Store PAT for attachment token into local storage
   *
   * @param {string} pat Personal Access Token for uploading attachments.
   */
  public saveAttachmentToken(pat: string): void {
    this.consultationStorageService.createAttachmentToken = pat;
  }

  /**
   * Clears all data associated with the last consultation request.
   */
  public clearLastConsultationRequestData(): void {
    this.indexedDbService.deleteDatabase(`${this.consultationStorageService.transactionId}-questionnaire`).subscribe();
    this.indexedDbService
      .deleteDatabase(`${this.consultationStorageService.transactionId}-consultation-attachments`)
      .subscribe();
    this.consultationStorageService.removeConsultationRequestCompletedData();
  }

  /**
   * Deletes all previous consultation databases if they exist.
   *
   * @param {string} transactionId the transaction ID of the current consultation order
   *
   * @returns {Observable<any[]>} an observable that emits the result of the deletion
   */
  public deletePreviousConsultationDbs(transactionId: string): Observable<any[]> {
    return this.indexedDbService.getAllDatabaseNames().pipe(
      switchMap((names) => {
        const dbsToDelete = names
          .filter(
            (name) =>
              (name.endsWith('-questionnaire') || name.endsWith('-consultation-attachments')) &&
              !name.startsWith(transactionId),
          )
          .map((name) => this.indexedDbService.deleteDatabase(name));

        return forkJoin(dbsToDelete);
      }),
      catchError((error) => {
        this.errorLoggingService.logError(error);

        return of([]);
      }),
    );
  }

  /**
   * Get the public manager user info.
   *
   * @param {number} userId Manager user id
   */
  public getManageUser(userId: number): Observable<{ id: number }> {
    return this.http.get<{ id: number }>(`${environment.analyteCareApi}/api/v1/users/${userId}`);
  }

  /**
   * Get the basic order details.
   *
   * @param {string} orderId Order Id
   * @param {string} hash The hash of the order
   * @param {Record<string, string>} customHeaders  Custom headers to be added to the request (key-value pairs)
   */
  public getOrderDetails(
    orderId: string,
    hash: string,
    customHeaders: Record<string, string> = {},
  ): Observable<HttpResponse<ConsultationRequestOrderDetail>> {
    let headers = this.createAuthorizationHeader(hash, orderId);
    Object.entries(customHeaders).forEach(([name, value]) => {
      headers = headers.append(name, value);
    });

    return this.http.get<ConsultationRequestOrderDetail>(`${this.apiConsultationUrl}/order/${orderId}`, {
      headers,
      observe: 'response',
    });
  }

  /**
   * Retrieves the current plan, available upgrades, and overdue balance for an order's subscription.
   *
   * @param {string} orderId the ID of the order
   * @param {string} hash    the hash associated with the order
   */
  public getSubscriptionDetails(orderId: string, hash: string): Observable<ConsultationSubscriptionDetails> {
    return this.http.get<ConsultationSubscriptionDetails>(`${this.apiUrl}/order/${orderId}/subscription/details`, {
      headers: new HttpHeaders({ 'X-API-Hash': hash }),
    });
  }

  /**
   * Get the details of the requested order.
   *
   * TODO: the treatmentType is a temporary fix, we need to consolidate DoxyPEP and remove it in the future.
   * Issue: https://github.com/Medology/ng-checkout/issues/2445
   *
   * @param {string}                     orderId        Order Id
   * @param {string}                     hash           The hash of the order
   * @param {ConsultationTreatmentTypes} _treatmentType The treatment type (only children classes could use it)
   * @param {Record<string, string>} customHeaders  Custom headers to be added to the request (key-value pairs)
   */
  public getConsultationOrderDetails(
    orderId: string,
    hash: string,
    _treatmentType: ConsultationTreatmentTypes,
    customHeaders: Record<string, string> = {},
  ): Observable<ConsultationRequestOrderDetail> {
    return this.getOrderDetails(orderId, hash, customHeaders).pipe(
      map((response: HttpResponse<ConsultationRequestOrderDetail>) => {
        const orderDetails = response.body;

        if (
          !orderDetails.subscription_current_intake_url &&
          orderDetails.status === ConsultationRequestOrderStatus.Canceled
        ) {
          throw new ConsultationRequestOrderCanceledError(environment.siteUrls.home, environment.domain);
        }

        orderDetails.anonymousToken = response.headers.get(this.anonymousTokenResponseHeaderName);

        return orderDetails;
      }),
    );
  }

  /**
   * Builds the absolute URL for the consultation request page.
   */
  public getConsultationPageUrl(
    treatmentType: ConsultationTreatmentTypes,
    transactionId: string,
    hash: string,
  ): string {
    const resolved =
      treatmentType === ConsultationTreatmentTypes.StarfishStd ? ConsultationTreatmentTypes.Std : treatmentType;

    const urlTree = this.router.createUrlTree([`/consultation/${resolved.replace(/_/g, '-')}/request`], {
      queryParams: { order_id: transactionId, hash },
    });

    return `${location.origin}${this.router.serializeUrl(urlTree)}`;
  }

  /**
   * Get consultation request discount.
   *
   * @returns the consultation request discount amount (cents)
   */
  public getDiscount(): number {
    const discount = this.formService.consultationRequest.get('payment').get('discount').value;
    if (isNaN(discount) || discount <= 0) {
      return 0;
    }

    return discount * 100;
  }

  /**
   * Get the subtotal to pay for the consultation request by multiplying the number of treatments
   * by the cost of each consultation request.
   *
   * @returns the subtotal amount (cents) to be paid
   */
  public getSubtotal(): number {
    const treatments = this.treatmentControl.value === Treatment.SelfAndPartner ? 2 : 1;

    return treatments * ConsultationRequestService.cost;
  }

  /**
   * Get consultation request total, by subtracting the discount from the subtotal.
   *
   * @returns the amount (cents) to be paid for the consultation request
   */
  public getTotal(): number {
    const total = this.getSubtotal() - this.getDiscount();

    return total > 0 ? total : 0;
  }

  /**
   * Check if the treatment preference has changed.
   */
  public hasTreatmentPreferenceChanged(): boolean {
    return this.consultationStorageService.treatmentPreferences !== this.getOrderedTreatmentPreferences();
  }

  /**
   * Get the ordered treatment preferences.
   */
  public getOrderedTreatmentPreferences(): string {
    let firstMedication = this.consultationStorageService.customerTests.find(({ test }) => test?.is_medication)?.test
      ?.name;

    if (firstMedication) {
      return firstMedication;
    }

    /**
     * We need to keep the code below because of backwards compatibility with DoxyPEP on STDcheck & Healthlabs.
     * When we add DoxyPEP to the STDcheck or HL order we create an order with StdPrevention treatmentType without
     * the medication. This means that we aren't able to know which preferred medication treatment the patient selected.
     *
     * Issue: https://github.com/Medology/arepa-general/issues/1437
     */
    return this.consultationStorageService.treatmentType === ConsultationTreatmentTypes.StdPrevention
      ? 'Doxycycline'
      : this.consultationOrderDetail?.tests.find(({ customer_tests_type }) =>
          [CustomerTestsTypes.Individual, CustomerTestsTypes.Panel].includes(customer_tests_type),
        ).customer_tests_name;
  }

  /**
   * Creates a basic consultation request (no payment processing).
   *
   * @param {BasicConsultationRequestRequest} requestBody basic consultation request request body
   * @param {string} hash hash id from the order
   * @param {string} orderId transaction id from the order
   *
   * @returns {Observable<BasicConsultationRequestResponse>} an observable that emits the consultation request response
   */
  public postBasicConsultationRequest(
    requestBody: BasicConsultationRequestRequest,
    hash: string,
    orderId: string,
  ): Observable<BasicConsultationRequestResponse> {
    return this.http.post<BasicConsultationRequestResponse>(`${this.apiConsultationUrl}/request/basic`, requestBody, {
      headers: this.createAuthorizationHeader(hash, orderId),
    });
  }

  /**
   * Request to retrieve a Consultation Request
   *
   * @param consultationRequestId the consultation request id
   * @param customerId            the order id
   * @param hash                  the hash of the order
   */
  public getConsultationRequest(
    consultationRequestId: string,
    customerId: string,
    hash: string,
  ): Observable<ConsultationRequest> {
    return this.http.get<any>(`${this.apiConsultationUrl}/request/${consultationRequestId}`, {
      headers: this.createAuthorizationHeader(hash, customerId),
    });
  }

  /**
   * Request to update a Consultation Request
   *
   * @param consultationRequestId the consultation request id
   * @param customerId            the order id
   * @param hash                  the hash of the order
   * @param body                  the body to the request
   */
  public update(consultationRequestId: string, customerId: string, hash: string, body: {}): Observable<any> {
    return this.http.put<any>(`${this.apiConsultationUrl}/request/${consultationRequestId}`, body, {
      headers: this.createAuthorizationHeader(hash, customerId),
    });
  }

  /**
   * Saves a payment method as the billing info.
   *
   * @param {AbstractSubscriptionBillingUpdateRequest} request the payment method to save
   * @param {string} transactionId the transaction ID of the order
   * @param {string} hash the hash of the order
   *
   * @returns {Observable<void>} an observable that completes once the billing info is updated
   */
  public updateSubscriptionBilling(
    request: AbstractSubscriptionBillingUpdateRequest,
    transactionId: string,
    hash: string,
  ): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/order/${transactionId}/subscription/billing`, request, {
      headers: new HttpHeaders({ 'X-API-Hash': hash }),
    });
  }

  /**
   * Stores an attachment for a specific consultation in an order.
   *
   * @param {string} transactionId the ID of the order
   * @param {string} hash the hash of the order
   * @param {string} consultationId the ID of the consultation request
   * @param {File} file the file to be attached to the consultation request
   * @param {string} type the type of the attachment
   *
   * @returns {Observable<any>} an observable that emits the server's response
   */
  public storeAttachment(
    consultationId: string,
    file: File,
    type: ConsultationRequestAttachmentType = ConsultationRequestAttachmentType.Others,
  ): Observable<any> {
    return from(this.fileService.getCompressedFile(file)).pipe(
      switchMap((compressedFile) => {
        const formData = new FormData();
        formData.append('attachment', compressedFile, compressedFile.name);
        formData.append('type', type);

        if (!this.consultationStorageService.createAttachmentToken) {
          throw new MissingStoreAttachmentTokenError(environment.email);
        }

        return this.http.post(`${this.apiV2Url}/consultation-requests/${consultationId}/attachments`, formData, {
          headers: new HttpHeaders({
            Authorization: `Bearer ${this.consultationStorageService.createAttachmentToken}`,
          }),
        });
      }),
    );
  }

  /**
   * Get the consultations of the requested order.
   *
   * @param {string} orderId Order Id
   * @param {string} hash    The hash of the order
   * @param {string} filters The filters to apply to the request
   */
  protected getConsultations(
    orderId: string,
    hash: string,
    filters: string,
  ): Observable<{ data: ConsultationRequest[] }> {
    return this.http.get<any>(`${this.apiUrl}/order/${orderId}/consultations`, {
      headers: this.createAuthorizationHeader(hash, orderId),
      params: new HttpParams().append('filters', filters),
    });
  }

  /**
   * Generate the Http Headers that enable us to authenticate into stdcheck
   *
   * @param hash        the hash of the order
   * @param customerId  the id of the order
   */
  private createAuthorizationHeader(hash: string, customerId: string): HttpHeaders {
    return new HttpHeaders({ 'X-API-Hash': hash, 'X-API-Id': customerId });
  }
}
